'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Award, Crown, HelpCircle } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import DashboardMetricCard from './DashboardMetricCard';
import TierBadge from '@/components/profile/TierBadge';

type TierRequirement = { key: string; current: number; required: number; met: boolean };
type TierNext = { name: string; requirements: TierRequirement[] };
type TierThresholds = {
  kyc: boolean;
  completeness: number;
  referrals: number;
  loginStreak: number;
  onLeaderboard: boolean;
};
type TierResult = { tier: string; current: TierThresholds; next: TierNext | null };

const REQ_LABELS: Record<string, string> = {
  kyc: 'Verify your identity',
  completeness: 'Profile completeness',
  referrals: 'Successful referrals',
  loginStreak: 'Login streak (days)',
  leaderboard: 'On a weekly leaderboard',
};

export default function TierCard() {
  const [data, setData] = useState<TierResult | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/proxy/api/profile/me/tier', undefined, { method: 'GET' });
        if (!res.ok) { if (!cancelled) setError(true); return; }
        const json = (await res.json()) as TierResult;
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const icon = <Award className="h-4 w-4" strokeWidth={1.75} />;

  if (error) {
    return (
      <DashboardMetricCard title="Tier" icon={icon}>
        <div className="text-sm text-muted-foreground">Couldn&apos;t load tier right now.</div>
      </DashboardMetricCard>
    );
  }
  if (data === null) {
    return (
      <DashboardMetricCard title="Tier" icon={icon}>
        <div className="h-9 w-24 rounded bg-muted animate-pulse" />
      </DashboardMetricCard>
    );
  }

  const TierIcon = data.tier === 'platinum' ? Crown : Award;

  return (
    <DashboardMetricCard title="Tier" icon={icon}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {data.tier === 'none' ? (
            <span className="text-base font-semibold text-muted-foreground">No tier yet</span>
          ) : (
            <>
              <TierBadge tier={data.tier} />
              <TierIcon className="h-5 w-5 text-muted-foreground" strokeWidth={1.75} />
            </>
          )}
        </div>
        <Link
          href="/help/tiers"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.75} />
          How tiers work
        </Link>
      </div>

      {data.next ? (
        <div>
          <div className="text-xs text-muted-foreground mb-2">
            Next: <span className="font-semibold capitalize text-foreground">{data.next.name}</span>
          </div>
          <ul className="space-y-1.5">
            {data.next.requirements.map((req) => {
              const label = REQ_LABELS[req.key] ?? req.key;
              const pct = req.required > 0 ? Math.min(100, (req.current / req.required) * 100) : 0;
              return (
                <li key={req.key}>
                  <div className="flex items-center justify-between text-xs mb-0.5">
                    <span className={req.met ? 'text-muted-foreground line-through' : ''}>{label}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {req.current}/{req.required}
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className={'h-full rounded-full transition-[width] duration-700 ease-out ' + (req.met ? 'bg-success' : '')}
                      style={{ width: `${pct}%`, ...(req.met ? {} : { backgroundImage: 'var(--brand-ribbon-hz)' }) }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">You&apos;ve reached the top tier.</div>
      )}
    </DashboardMetricCard>
  );
}
